import React, { useContext, useRef, useState } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { useForm } from "react-hook-form";
import { Link, navigate } from "@reach/router";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { FormErrors } from "@squirrellearningltd/go-teacher-components";
import { Calendar, Check, Info } from "react-feather";
import BackButton from "../../../components/BackButton";
import * as Yup from "yup";
import Header from "../../../components/Header";
import BackLink from "../../../components/BackLink";
import { AuthContext } from "../../../services/user/AuthProvider";
import { GET_FRANCHISE, GET_FRANCHISE_DETAILS, CREATE_FRANCHISE_SCHOOL } from "../services/graphql";
import { GET_COUNTRIES } from "../../Invoices/services/graphql";
import styles from "../Franchises.module.scss";

const AddFranchiseSchool = ({ franchiseId, portal = false }) => {
  const { currentUser } = useContext(AuthContext);
  const [step, setStep] = useState(1);
  const [createdSchool, setCreatedSchool] = useState(null);
  const [formErrors, setFormErrors] = useState([]);
  const saving = useRef(false);
  const [createFranchiseSchool, { loading: creating }] = useMutation(CREATE_FRANCHISE_SCHOOL, {
    refetchQueries: [{ query: GET_FRANCHISE, variables: { franchiseId } }]
  });
  const [schoolDetails, setSchoolDetails] = useState(null);
  const [licenceStartDate, setLicenceStartDate] = useState(() => moment().startOf("day").toDate());
  const [licenceEndDate, setLicenceEndDate] = useState(() => moment().startOf("day").add(1, "year").subtract(1, "day").toDate());
  const startDatePicker = useRef(null);
  const endDatePicker = useRef(null);
  const { data, loading, error } = useQuery(GET_FRANCHISE_DETAILS, {
    variables: { franchiseId },
    fetchPolicy: "cache-first"
  });
  const { data: countriesData } = useQuery(GET_COUNTRIES);
  const franchise = data && data.franchise;
  const countries = (countriesData && countriesData.countriesList) || [];
  const country = franchise && countries.find(item => item.code === franchise.countryCode);
  const schoolsPath = portal ? `/franchise-portal/${franchiseId}/schools` : `/franchises/${franchiseId}/schools`;
  const schoolSchema = {
    name: Yup.string().trim().required("Please enter a school name"),
    address: Yup.string().trim().required("Please enter an address"),
    address2: Yup.string().trim(),
    city: Yup.string().trim().required("Please enter a city or governorate"),
    postcode: Yup.string().trim(),
    country: Yup.string().required("The franchise must have a country set"),
    contactName: Yup.string().trim().required("Please enter a school contact name"),
    contactEmail: Yup.string().trim().email("Please enter a valid email address").required("Please enter a school contact email"),
    schoolPhoneNumber: Yup.string().trim()
  };
  const administratorSchema = {
    adminFirstName: Yup.string().trim(),
    adminLastName: Yup.string().trim(),
    adminEmail: Yup.string().trim().email("Please enter a valid email address"),
    adminRole: Yup.string().trim(),
    telephone: Yup.string().trim(),
    sendAdminInvitation: Yup.boolean(),
    pupilLicences: Yup.number().transform((value, originalValue) => originalValue === "" ? undefined : value).typeError("Please enter the pupil licence quantity").integer("Please enter a whole number").positive("Pupil licence quantity must be greater than zero"),
    licenceStartDate: Yup.string().test("start-date", "Please select a valid start date", value => !value || moment(value, "YYYY-MM-DD", true).isValid()),
    licenceEndDate: Yup.string().test("valid-end-date", "Please select a valid end date", value => !value || moment(value, "YYYY-MM-DD", true).isValid()).test("end-date", "End date cannot be before start date", function (value) {
      return !value || !this.parent.licenceStartDate || value >= this.parent.licenceStartDate;
    })
  };
  const activationSchema = Yup.object().shape({
    ...schoolSchema,
    ...administratorSchema,
    adminFirstName: administratorSchema.adminFirstName.required(),
    adminLastName: administratorSchema.adminLastName.required(),
    adminEmail: administratorSchema.adminEmail.required(),
    pupilLicences: administratorSchema.pupilLicences.required(),
    licenceStartDate: administratorSchema.licenceStartDate.required(),
    licenceEndDate: administratorSchema.licenceEndDate.required()
  });
  const canActivate = !!schoolDetails && activationSchema.isValidSync(schoolDetails);
  const schema = Yup.object().shape(step === 1 ? schoolSchema : { ...schoolSchema, ...administratorSchema });
  const { register, handleSubmit, errors, clearError, reset } = useForm({
    validationSchema: schema,
    mode: "onSubmit"
  });

  const goToStep = nextStep => {
    if (saving.current) return;
    clearError();
    setFormErrors([]);
    setStep(nextStep);
    window.scrollTo(0, 0);
  };

  const onSubmit = values => {
    if (saving.current || createdSchool) return;
    if (step < 3) {
      setSchoolDetails(values);
      goToStep(step + 1);
      return;
    }
    saveSchool(values, false);
  };

  const saveSchool = (values, draft) => {
    if (saving.current || createdSchool) return;
    if (!draft && !activationSchema.isValidSync(values)) return;
    const variables = { franchiseId, country: franchise.countryCode };
    Object.keys(values).forEach(key => {
      const value = values[key];
      if (value !== "" && value !== null && value !== undefined) {
        variables[key] = value;
      }
    });
    if (draft) {
      variables.onboardingStatus = "draft";
      variables.sendAdminInvitation = false;
    }
    setSchoolDetails(values);
    saving.current = true;
    setFormErrors([]);
    createFranchiseSchool({
      variables
    }).then(({ data }) => {
      const result = data && data.createFranchiseSchool;
      if (result && result.errors && result.errors.length > 0) {
        setFormErrors(result.errors);
      } else if (result && result.school && result.school.id) {
        if (draft) {
          navigate(`${schoolsPath}/${result.school.id}`);
          return;
        } else {
          setCreatedSchool(result.school);
          window.scrollTo(0, 0);
        }
      } else {
        setFormErrors(["Unable to save the school. Please try again."]);
      }
      saving.current = false;
    }).catch(error => {
      saving.current = false;
      setFormErrors([error.message]);
    });
  };

  const addAnotherSchool = () => {
    const startDate = moment().startOf("day");
    const endDate = startDate.clone().add(1, "year").subtract(1, "day");
    reset({
      sendAdminInvitation: true,
      country: franchise.countryCode,
      licenceStartDate: startDate.format("YYYY-MM-DD"),
      licenceEndDate: endDate.format("YYYY-MM-DD")
    });
    setLicenceStartDate(startDate.toDate());
    setLicenceEndDate(endDate.toDate());
    setSchoolDetails(null);
    setCreatedSchool(null);
    goToStep(1);
  };

  const administratorName = schoolDetails ? [schoolDetails.adminFirstName, schoolDetails.adminLastName].filter(Boolean).join(" ") : "";
  const dashboardPath = portal ? `/franchise-portal/${franchiseId}` : `/franchises/${franchiseId}`;

  return (
    <div className={portal ? `${styles.franchises} ${styles.portal}` : styles.franchises}>
      {portal && <Header user={currentUser} portalFranchise={franchise} portalFranchiseId={franchiseId} portalPage="schools" />}
      {!createdSchool && (step === 1 ? <BackLink text="Schools" to={schoolsPath} /> : <BackButton text={step === 2 ? "School details" : "Administrator & licences"} onClick={() => goToStep(step - 1)} />)}
      {loading && <p role="status">Loading franchise...</p>}
      {error && <p role="alert">Unable to load franchise. Please try again later.</p>}
      {!loading && !error && !franchise && <p>Franchise not found.</p>}
      {!loading && !error && franchise && <>
        <div hidden={!!createdSchool}>
        <div className={styles.schoolFormHeading}>
          <h1 className="title">{step === 3 ? "Review and activate" : "Add a new school"}</h1>
          <p>{step === 1 ? `Create the ${franchise.name} school account without the standard UK onboarding journey.` : step === 2 ? "Set up the school’s first administrator and annual pupil licence period." : "Check the information below before creating the school on BoomReader."}</p>
        </div>
        <ol className={styles.schoolSteps} aria-label="School creation steps">
          <li className={step === 1 ? styles.currentStep : styles.completedStep} aria-current={step === 1 ? "step" : undefined}><span>{step === 1 ? "1" : <Check size={18} aria-hidden="true" />}</span>School details</li>
          <li className={step === 2 ? styles.currentStep : step === 3 && canActivate ? styles.completedStep : undefined} aria-current={step === 2 ? "step" : undefined}><span>{step === 3 && canActivate ? <Check size={18} aria-hidden="true" /> : "2"}</span>Administrator &amp; licences</li>
          <li className={step === 3 ? styles.currentStep : undefined} aria-current={step === 3 ? "step" : undefined}><span>3</span>Review &amp; activate</li>
        </ol>
        <form className={`${styles.territoryForm} ${styles.schoolForm}`} onSubmit={handleSubmit(onSubmit)} noValidate>
          <div hidden={step !== 1}>
          <div className={styles.detailPanel}>
            <h2>School details</h2>
            <p className={styles.schoolFormDescription}>Enter the information that will identify this school throughout BoomReader.</p>
            <div className={styles.schoolNameRow}>
              <div className="basic-form__group-full">
                <label htmlFor="name" className="basic-form__text-label">School name *</label>
                <input id="name" name="name" type="text" className="basic-form__text-box" placeholder="e.g. Cairo International Academy" ref={register} aria-required="true" />
                {errors.name && <p className="basic-form__hint u-m-top-0">{errors.name.message}</p>}
              </div>
              <div className="basic-form__group-full">
                <label htmlFor="schoolCode" className="basic-form__text-label">School code</label>
                <input id="schoolCode" type="text" className="basic-form__text-box" placeholder="Generated automatically" readOnly />
              </div>
            </div>
            <div className={styles.schoolFormTwoColumns}>
              <div className="basic-form__group-full">
                <label htmlFor="address" className="basic-form__text-label">Address line 1 *</label>
                <input id="address" name="address" type="text" className="basic-form__text-box" placeholder="Building and street" ref={register} aria-required="true" />
                {errors.address && <p className="basic-form__hint u-m-top-0">{errors.address.message}</p>}
              </div>
              <div className="basic-form__group-full">
                <label htmlFor="address2" className="basic-form__text-label">Address line 2</label>
                <input id="address2" name="address2" type="text" className="basic-form__text-box" placeholder="Optional" ref={register} />
              </div>
            </div>
            <div className={styles.schoolFormThreeColumns}>
              <div className="basic-form__group-full">
                <label htmlFor="city" className="basic-form__text-label">City / Governorate *</label>
                <input id="city" name="city" type="text" className="basic-form__text-box" placeholder="" ref={register} aria-required="true" />
                {errors.city && <p className="basic-form__hint u-m-top-0">{errors.city.message}</p>}
              </div>
              <div className="basic-form__group-full">
                <label htmlFor="postcode" className="basic-form__text-label">Postcode</label>
                <input id="postcode" name="postcode" type="text" className="basic-form__text-box" placeholder="Optional" ref={register} />
              </div>
              <div className="basic-form__group-full">
                <label htmlFor="country" className="basic-form__text-label">Country *</label>
                <input id="country" type="text" className="basic-form__text-box" value={country ? country.name : franchise.countryCode || ""} readOnly aria-required="true" />
                <input type="hidden" name="country" value={franchise.countryCode || ""} ref={register} />
                {errors.country && <p className="basic-form__hint u-m-top-0">{errors.country.message}</p>}
              </div>
            </div>
            <div className={styles.schoolFormTwoColumns}>
              <div className="basic-form__group-full">
                <label htmlFor="contactName" className="basic-form__text-label">School contact name *</label>
                <input id="contactName" name="contactName" type="text" className="basic-form__text-box" placeholder="Main operational contact" ref={register} aria-required="true" />
                {errors.contactName && <p className="basic-form__hint u-m-top-0">{errors.contactName.message}</p>}
              </div>
              <div className="basic-form__group-full">
                <label htmlFor="contactEmail" className="basic-form__text-label">School contact email *</label>
                <input id="contactEmail" name="contactEmail" type="email" className="basic-form__text-box" placeholder="name@school.edu" ref={register} aria-required="true" />
                {errors.contactEmail && <p className="basic-form__hint u-m-top-0">{errors.contactEmail.message}</p>}
              </div>
            </div>
            <div className={styles.schoolFormThreeColumns}>
              <div className="basic-form__group-full">
                <label htmlFor="schoolPhoneNumber" className="basic-form__text-label">Telephone</label>
                <input id="schoolPhoneNumber" name="schoolPhoneNumber" type="tel" className="basic-form__text-box" placeholder="" ref={register} />
              </div>
              <div className="basic-form__group-full">
                <label htmlFor="schoolType" className="basic-form__text-label">School type</label>
                <select id="schoolType" className="basic-form__text-select" disabled><option value="">Select school type</option></select>
              </div>
              <div className="basic-form__group-full">
                <label htmlFor="curriculumId" className="basic-form__text-label">Curriculum</label>
                <select id="curriculumId" className="basic-form__text-select" disabled><option value="">Select curriculum</option></select>
              </div>
            </div>
            <p className={styles.schoolFormNotice}>The school can be edited later. Required fields must be completed before activation.</p>
          </div>
          </div>
          <div hidden={step !== 2}>
            <div className={styles.detailPanel}>
              <h2>Primary administrator</h2>
              <p className={styles.schoolFormDescription}>This person receives the first login invitation and can manage the school.</p>
              <div className={styles.schoolFormThreeColumns}>
                <div>
                  <label htmlFor="adminFirstName" className="basic-form__text-label">First name</label>
                  <input id="adminFirstName" name="adminFirstName" className="basic-form__text-box" ref={register} />
                  {errors.adminFirstName && <p className="basic-form__hint u-m-top-0">{errors.adminFirstName.message}</p>}
                </div>
                <div>
                  <label htmlFor="adminLastName" className="basic-form__text-label">Last name</label>
                  <input id="adminLastName" name="adminLastName" className="basic-form__text-box" ref={register} />
                  {errors.adminLastName && <p className="basic-form__hint u-m-top-0">{errors.adminLastName.message}</p>}
                </div>
                <div>
                  <label htmlFor="adminEmail" className="basic-form__text-label">Email address</label>
                  <input id="adminEmail" name="adminEmail" type="email" className="basic-form__text-box" ref={register} />
                  {errors.adminEmail && <p className="basic-form__hint u-m-top-0">{errors.adminEmail.message}</p>}
                </div>
              </div>
              <div className={styles.schoolFormTwoColumns}>
                <div>
                  <label htmlFor="adminRole" className="basic-form__text-label">Role / job title</label>
                  <input id="adminRole" name="adminRole" className="basic-form__text-box" placeholder="School administrator" ref={register} />
                </div>
                <div>
                  <label htmlFor="telephone" className="basic-form__text-label">Telephone</label>
                  <input id="telephone" name="telephone" type="tel" className="basic-form__text-box" ref={register} />
                </div>
              </div>
              <div className={styles.invitationRow}>
                <div className="basic-form__switch">
                  <input id="sendAdminInvitation" name="sendAdminInvitation" type="checkbox" className="switch-input" defaultChecked ref={register} />
                  <label htmlFor="sendAdminInvitation" className="switch-label"><span className={styles.visuallyHidden}>Send administrator invitation</span></label>
                </div>
                <label htmlFor="sendAdminInvitation">Send the administrator invitation when the school is activated</label>
              </div>
              <div className={styles.licenceSection}>
                <h2>Annual pupil licences</h2>
                <p className={styles.schoolFormDescription}>Only pupil accounts require a licence. Teacher and parent accounts are not included in the licence quantity.</p>
                <div className={styles.schoolFormThreeColumns}>
                  <div>
                    <label htmlFor="pupilLicences" className="basic-form__text-label">Pupil licence quantity</label>
                    <input id="pupilLicences" name="pupilLicences" type="number" min="1" step="1" className="basic-form__text-box" ref={register} />
                    {errors.pupilLicences && <p className="basic-form__hint u-m-top-0">{errors.pupilLicences.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="licenceStartDatePicker" className="basic-form__text-label">Start date</label>
                    <div className={styles.licenceDate}>
                      <DatePicker id="licenceStartDatePicker" ref={startDatePicker} selected={licenceStartDate} onChange={date => setLicenceStartDate(date)} dateFormat="dd/MM/yyyy" className="basic-form__text-box" wrapperClassName={styles.datePickerWrapper} />
                      <button type="button" aria-label="Choose start date" onClick={() => startDatePicker.current.setOpen(true)}><Calendar size={18} aria-hidden="true" /></button>
                    </div>
                    <input type="hidden" name="licenceStartDate" value={licenceStartDate ? moment(licenceStartDate).format("YYYY-MM-DD") : ""} ref={register} />
                    {errors.licenceStartDate && <p className="basic-form__hint u-m-top-0">{errors.licenceStartDate.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="licenceEndDatePicker" className="basic-form__text-label">End date</label>
                    <div className={styles.licenceDate}>
                      <DatePicker id="licenceEndDatePicker" ref={endDatePicker} selected={licenceEndDate} onChange={date => setLicenceEndDate(date)} dateFormat="dd/MM/yyyy" className="basic-form__text-box" wrapperClassName={styles.datePickerWrapper} />
                      <button type="button" aria-label="Choose end date" onClick={() => endDatePicker.current.setOpen(true)}><Calendar size={18} aria-hidden="true" /></button>
                    </div>
                    <input type="hidden" name="licenceEndDate" value={licenceEndDate ? moment(licenceEndDate).format("YYYY-MM-DD") : ""} ref={register} />
                    {errors.licenceEndDate && <p className="basic-form__hint u-m-top-0">{errors.licenceEndDate.message}</p>}
                  </div>
                </div>
                <p className={styles.schoolFormNotice}>The dates default to today and one year minus one day later. Both can be changed. Financial calculations will be added later.</p>
              </div>
            </div>
          </div>
          {step === 3 && schoolDetails && <>
            <div className={styles.schoolReviewCards}>
              <div className={styles.detailPanel}>
                <div className={styles.directoryHeading}>
                  <h2>School details</h2>
                  <button type="button" className={styles.textButton} disabled={creating} onClick={() => goToStep(1)}>Edit</button>
                </div>
                <dl className={styles.schoolReviewDetails}>
                  <dt>School</dt><dd>{schoolDetails.name}</dd>
                  <dt>Code</dt><dd>Generated automatically</dd>
                  <dt>Address</dt><dd>{[schoolDetails.address, schoolDetails.address2, schoolDetails.city, schoolDetails.postcode].filter(Boolean).join(", ")}</dd>
                  <dt>Contact</dt><dd>{schoolDetails.contactName}</dd>
                  <dt>Email</dt><dd>{schoolDetails.contactEmail}</dd>
                </dl>
              </div>
              <div className={styles.detailPanel}>
                <div className={styles.directoryHeading}>
                  <h2>Administrator &amp; licences</h2>
                  <button type="button" className={styles.textButton} disabled={creating} onClick={() => goToStep(2)}>Edit</button>
                </div>
                <dl className={styles.schoolReviewDetails}>
                  <dt>Administrator</dt><dd>{administratorName || "—"}</dd>
                  <dt>Invite</dt><dd>{schoolDetails.sendAdminInvitation ? "Send on activation" : "Do not send on activation"}</dd>
                  <dt>Pupil licences</dt><dd>{schoolDetails.pupilLicences || 0}</dd>
                  <dt>Start date</dt><dd>{schoolDetails.licenceStartDate ? moment(schoolDetails.licenceStartDate).format("D MMMM YYYY") : "—"}</dd>
                  <dt>End date</dt><dd>{schoolDetails.licenceEndDate ? moment(schoolDetails.licenceEndDate).format("D MMMM YYYY") : "—"}</dd>
                </dl>
              </div>
            </div>
            {canActivate && <div className={`${styles.detailPanel} ${styles.activationChecklist}`}>
              <h2>Activation checklist</h2>
              <ul>
                <li><Check size={20} aria-hidden="true" />School account will be created</li>
                <li><Check size={20} aria-hidden="true" />{schoolDetails.pupilLicences} pupil licences will begin on {moment(schoolDetails.licenceStartDate).format("D MMMM YYYY")}</li>
                <li><Check size={20} aria-hidden="true" />{schoolDetails.sendAdminInvitation ? `${administratorName} will receive a secure setup invitation` : "No administrator invitation will be sent on activation"}</li>
                <li><Check size={20} aria-hidden="true" />The school will appear immediately in the {franchise.name} directory</li>
              </ul>
            </div>}
            <div className={`${styles.detailPanel} ${styles.activationNote}`}>
              <Info size={24} aria-hidden="true" />
              <p>No price or commission is calculated at this stage. Activation records the licence period only.</p>
            </div>
          </>}
          {formErrors.length > 0 && <FormErrors errors={formErrors} />}
          <div className={styles.schoolFormFooter}>
            <span>Step {step} of 3</span>
            <div className={styles.formActions}>
              {step === 1 ? <Link to={schoolsPath} className={styles.button}>Cancel</Link> : <button type="button" className={styles.button} disabled={creating} onClick={() => goToStep(step - 1)}>Back</button>}
              {step === 3 && <button type="button" className={styles.button} disabled={creating} onClick={handleSubmit(values => saveSchool(values, true))}>Save as draft</button>}
              <button type="submit" className={styles.primaryButton} disabled={creating || (step === 3 && !canActivate)}>{creating ? "Saving..." : step === 3 ? "Activate school" : "Continue"}</button>
            </div>
          </div>
        </form>
        </div>
        {createdSchool && <>
          <div className={`${styles.detailPanel} ${styles.schoolSuccess}`}>
            <span className={styles.schoolSuccessIcon}><Check size={56} aria-hidden="true" /></span>
            <h1 className="title">School activated</h1>
            <p>{createdSchool.name} is now live in the {franchise.name} franchise.</p>
            <div className={styles.schoolSuccessSummary}>
              <div className={styles.schoolFormThreeColumns}>
                <div><h3>School code</h3><strong>{createdSchool.schoolCode || "—"}</strong></div>
                <div><h3>Pupil licences</h3><strong>{schoolDetails.pupilLicences}</strong></div>
                <div><h3>Start date</h3><strong>{moment(schoolDetails.licenceStartDate).format("D MMM YYYY")}</strong></div>
              </div>
              <p>{schoolDetails.sendAdminInvitation ? `Administrator invitation requested for ${schoolDetails.adminEmail}` : "Administrator invitation was not requested."}</p>
            </div>
            <div className={styles.formActions}>
              <Link to={`${schoolsPath}/${createdSchool.id}`} className={styles.primaryButton}>Open school</Link>
              <button type="button" className={styles.button} onClick={addAnotherSchool}>Add another school</button>
            </div>
            <Link to={dashboardPath} className={styles.textButton}>Return to dashboard</Link>
          </div>
          <div className={styles.section}>
            <h2>What happens next?</h2>
            <ol className={styles.schoolSteps}>
              <li><span>1</span>{schoolDetails.sendAdminInvitation ? `${administratorName} creates their password` : "The administrator can be invited later"}</li>
              <li><span>2</span>School staff and classes can be added</li>
              <li><span>3</span>Licence usage appears on the {franchise.name} dashboard</li>
            </ol>
          </div>
        </>}
      </>}
    </div>
  );
};

export default AddFranchiseSchool;
